/**
 * Access Control was fifteen sidebar entries deep, several of them two views of
 * the same question — and one role's configuration was spread across three of
 * them, each re-picking the role and saving on its own.
 *
 * It is grouped here by the question each answers: who holds a role, what the
 * platform enforces, what happened. Everything about a single role now lives on
 * one screen (RoleAccessStudio), so the tabs that used to slice it up resolve
 * there instead of disappearing — a bookmarked
 * /superadmin/access-control/module-permissions still lands somewhere useful.
 *
 * Kept free of React so the id resolution can be tested on its own.
 */

export const ACCESS_GROUPS = [
  {
    label: 'Overview',
    tabs: [{ id: 'dashboard', label: 'Overview' }],
  },
  {
    label: 'Roles',
    tabs: [
      { id: 'roles', label: 'Roles & access' },
      { id: 'user-permissions', label: 'User overrides' },
      { id: 'permission-groups', label: 'Permission groups' },
      { id: 'group-permissions', label: 'Tenant assignment' },
    ],
  },
  {
    label: 'Platform',
    tabs: [
      { id: 'platform-matrix', label: 'Platform matrix' },
      { id: 'matrix', label: 'Tenant matrix' },
      { id: 'policies', label: 'Access policies' },
      { id: 'staff', label: 'Platform users' },
    ],
  },
  {
    label: 'Activity',
    tabs: [
      { id: 'audit', label: 'Audit log' },
      { id: 'simulator', label: 'Simulator' },
    ],
  },
];

export const ACCESS_PREFIX = 'access-control/';

/**
 * Tabs that were folded into the role screen. They are still reachable ids —
 * bookmarks, the older sidebar, anything a runbook wrote down — and they all
 * mean "show me this role", so they resolve there rather than falling back to
 * Overview and looking like the feature was removed.
 */
export const FOLDED_INTO_ROLES = {
  'role-permissions': 'roles',
  'module-permissions': 'roles',
  'menu-permissions': 'roles',
  directory: 'roles',
  permissions: 'roles',
};

/** Each sidebar entry points at the first tab of its group. */
export const ACCESS_NAV_ITEMS = ACCESS_GROUPS.map((group) => ({
  id: `${ACCESS_PREFIX}${group.tabs[0].id}`,
  label: group.label,
}));

const resolve = (view) => FOLDED_INTO_ROLES[view] || view;

export const accessGroupFor = (view) => {
  const target = resolve(view);
  return ACCESS_GROUPS.find((group) => group.tabs.some((tab) => tab.id === target)) || ACCESS_GROUPS[0];
};

/** The tab actually shown for a view — unknown ids fall back to the group's first. */
export const accessViewFor = (view) => {
  const target = resolve(view);
  const group = accessGroupFor(target);
  return group.tabs.some((tab) => tab.id === target) ? target : group.tabs[0].id;
};

/** Which sidebar entry to light up for any access-control view, old id or new. */
export const accessNavIdFor = (view) => `${ACCESS_PREFIX}${accessGroupFor(view).tabs[0].id}`;

/**
 * Views that read or write one tenant's access. Every one of these ran against a
 * hardcoded tenant 1 regardless of what the operator was looking at, so editing
 * "tenant 12's permissions" silently edited tenant 1's. The workspace shows a
 * tenant picker for exactly this set and passes the choice down.
 */
export const TENANT_SCOPED_VIEWS = [
  'roles', 'matrix', 'group-permissions', 'simulator',
];

export const isTenantScopedView = (view) => TENANT_SCOPED_VIEWS.includes(accessViewFor(view));
